<template>
  <div class="MapCheckInCard">
    <div class="location-label">
      <GamIcon :name="LocationHelper.getLocationTypeIcon(location.type)" size="16px" is-current />
      <span class="name">{{ location.name || location.address || localize('locations.detail.new_spot') }}</span>
    </div>

    <div class="actions">
      <label class="chess-set-toggle" @click.prevent="withChessSet = !withChessSet">
        <span class="chess-set-checkbox" :class="{ 'is-checked': withChessSet }">
          <GamIcon v-if="withChessSet" :name="GamIconName.CHECK" :size="GamIconSize.SMALL" />
        </span>
        <GamIcon :name="GamIconName.CHESS_SET" :size="GamIconSize.LARGE" class="chess-set-icon" />
      </label>

      <GamButton
        :variant="GamButtonVariant.HYPE"
        :size="GamButtonSize.SMALL"
        :left-icon="GamIconName.PLAY_EMPTY"
        :is-loading="isSubmitting"
        :is-disabled="isSubmitting"
        is-drop-shadow
        @click="handleCheckIn"
      >
        {{ localize('locations.detail.actions.checkIn') }}
      </GamButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { MapLocationDto } from '@/core/data/location/location.interface';
import { LocationType } from '@/core/data/location/location.type';
import { gambits, localize } from '@/core/gambits';
import LocationHelper from '@/core/helpers/location.helper';
import { useMapStore } from '@/stores/map.store';
import { useNavigationStore } from '@/stores/navigation.store';
import GamButton from '@/views/components/GamButton.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { handleCreateLocation } from '@/views/main/locations/side-panels/locationActions';
import { ref } from 'vue';

interface Props {
  location: MapLocationDto;
}

const props = defineProps<Props>();
const emit = defineEmits<{ 'checked-in': [] }>();

const mapStore = useMapStore();
const navigationStore = useNavigationStore();
const isSubmitting = ref(false);
const withChessSet = ref(false);

async function resolveLocationId(): Promise<string | null> {
  if (props.location.id !== mapStore.VIRTUAL_LOCATION_ID) {
    return props.location.id;
  }

  const locationProps = {
    name: props.location.name || props.location.address || 'New spot',
    address: props.location.address || '',
    type: LocationType.CHESS_FRIENDLY,
    generalFeatures: [],
    chessFeatures: [],
    coordinates: props.location.coordinates,
    photos: [],
    private: false,
  };

  const result = await handleCreateLocation(locationProps);
  if (!result || typeof result !== 'object' || !('id' in result)) return null;
  return (result as MapLocationDto).id;
}

async function handleCheckIn() {
  isSubmitting.value = true;

  const locationId = await resolveLocationId();
  if (!locationId) {
    isSubmitting.value = false;
    return;
  }

  await gambits.locationService.checkIn(locationId);

  if (withChessSet.value) {
    await gambits.locationService.updateCheckIn(locationId, { chessSetCount: 1 });
  }

  navigationStore.setActiveCheckIn({
    locationId,
    locationName: props.location.name || props.location.address || 'Spot',
    coordinates: props.location.coordinates,
    checkedInAt: new Date().toISOString(),
  });

  isSubmitting.value = false;
  emit('checked-in');
}
</script>

<style lang="scss" scoped>
.MapCheckInCard {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-l);
  background: var(--color-fabric-gradient-dark);
  border-radius: var(--radius-normal);
  min-height: 56px;
}

.location-label {
  display: flex;
  align-items: center;
  gap: 0.5em;
  min-width: 0;
  flex: 1;
}

.name {
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex-shrink: 0;
}

.chess-set-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.chess-set-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid var(--color-white-30);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition:
    background-color 0.15s,
    border-color 0.15s;

  &.is-checked {
    background-color: var(--color-pink);
    border-color: var(--color-pink);
  }
}

.chess-set-icon {
  color: var(--color-white);
}
</style>
